import { getGlobalVariables } from '@libs/core';
import { TButtonData, TCalculatorData, TCalculatorTopUpData, TCommonData, TextInputEnum, TInputData, TTextData, TTimerButtonData, TWidget } from '@libs/types';
import { GoogleOAuthProvider } from '@react-oauth/google';
import React from 'react';
import {
    TApplePay,
    TBalanceData,
    TBar,
    TBrazeBanner,
    TButtonRowEmphasised,
    TCardBalanceData,
    TCardView,
    TCarouselData,
    TConsumerInputPhoneData,
    TConsumerListData,
    TDetailsSeparateTextData,
    TGooglePay,
    TInfoBannerData,
    TInputPasscodeData,
    TInputSearchAddress,
    TKYC,
    TMpiData,
    TReferralsProgressData,
    TRowActionData,
    TSelectorRegularData,
    TSeparatorData,
    TSignInWithAppleData,
    TSignInWithGoogleData,
    TWebViewLayout,
} from '../types/data';
import {
    ActionWidgetEnum,
    AnimationIconEnum,
    BarEnum,
    ButtonEnum,
    CalculatorEnum,
    CardEnum,
    CarouselEnum,
    CheckboxEnum,
    DropdownEnum,
    ImageEnum,
    InputEnum,
    KycEnum,
    LayoutEnum,
    ListEnum,
    MpiEnum,
    RowEnum,
    SeparatorEnum,
    StoriesEnum,
    TextEnum,
    WidgetEnum,
} from '../types/enums';
import {
    TBrazeOption,
    TButtonOption,
    TDetailsSeparateTextOption,
    TInfoBannerOptions,
    TInputOption,
    TListOption,
    TOTPOption,
    TPasscodeOption,
    TRowOption,
    TSelectorRegularOption,
    TSeparatorOption,
    TSignInWithGoogleOptions,
    TWebViewLayoutOption,
} from '../types/options';
import { Action } from '../widgets/Action/Action';
import { ErrorAnimation } from '../widgets/Animation/ErrorAnimation';
import { LoadingAnimation } from '../widgets/Animation/LoadingAnimation';
import { SuccessAnimation } from '../widgets/Animation/SuccessAnimation';
import { LimitBar } from '../widgets/Bars/LimitBar';
import { ReferralsProgress } from '../widgets/Bars/ReferralsProgress';
import { ButtonApplePay } from '../widgets/Buttons/ButtonApplePay';
import { ButtonBanner } from '../widgets/Buttons/ButtonBanner';
import { ButtonCircleAnimated } from '../widgets/Buttons/ButtonCircleAnimated';
import { ButtonCircleWidget } from '../widgets/Buttons/ButtonCircleWidget';
import { ButtonGooglePay } from '../widgets/Buttons/ButtonGooglePay';
import { ButtonMenu } from '../widgets/Buttons/ButtonMenu';
import { ButtonMessage } from '../widgets/Buttons/ButtonMessage';
import { ButtonPillWidget } from '../widgets/Buttons/ButtonPillWidget';
import { ButtonRowTimer } from '../widgets/Buttons/ButtonRowTimer';
import { ButtonRowWidget } from '../widgets/Buttons/ButtonRowWidget';
import { ButtonSignInWithApple } from '../widgets/Buttons/ButtonSignInWithApple';
import { ButtonSignInWithGoogle } from '../widgets/Buttons/ButtonSignInWithGoogle';
import { DetailsSeparateText } from '../widgets/Buttons/DetailsSeparateText';
import { InfoBanner } from '../widgets/Buttons/InfoBanner';
import { RowActionWidget } from '../widgets/Buttons/RowActionWidget';
import { RowEmphasised } from '../widgets/Buttons/RowEmphasised';
import { TimerButton } from '../widgets/Buttons/TimerButton';
import { Calculator } from '../widgets/Calculactor/Calculator';
import { CalculatorTopUp } from '../widgets/Calculactor/CalculatorTopUp';
import { CardBalance } from '../widgets/Card/CardBalance/CardBalance';
import { CardInput } from '../widgets/Card/CardInput';
import { CardViewWidget } from '../widgets/Card/CardViewWidget';
import { CarouselWidget } from '../widgets/Carousel/CarouselWidget';
import { Checkbox } from '../widgets/Checkbox/Checkbox';
import { SwitcherWidget } from '../widgets/Checkbox/SwitcherWidget';
import { Dropdown } from '../widgets/Dropdown/Dropdown';
import { ProfileImage } from '../widgets/Image/profile/ProfileImage';
import { VisualWidget } from '../widgets/Image/VisualWidget';
import { InputDate } from '../widgets/Inputs/InputDate';
import { InputPasscode } from '../widgets/Inputs/InputPasscode';
import { InputPhoneWidget } from '../widgets/Inputs/InputPhoneWidget';
import { InputSearchAddress } from '../widgets/Inputs/InputSearchAddress';
import { OtpInputWidget } from '../widgets/Inputs/OtpInputWidget';
import { StrokedInput } from '../widgets/Inputs/StrokedInput';
import { Sumsub } from '../widgets/KYC/Sumsub';
import { ZendeskChat } from '../widgets/KYC/ZendeskChat';
import { FloatingLayoutWidget } from '../widgets/layout/FloatingLayoutWidget';
import { HorizontalLayoutWidget } from '../widgets/layout/HorizontalLayoutWidget';
import { SeparateContentLayout } from '../widgets/layout/SeparateContentLayout';
import { VerticalLayoutWide } from '../widgets/layout/VerticalLayoutWide';
import { VerticalLayoutWidget } from '../widgets/layout/VerticalLayoutWidget';
import { WebViewLayout } from '../widgets/layout/WebViewLayout';
import { CurrencyListWidget } from '../widgets/List/currency/CurrencyListWidget';
import { ListContacts } from '../widgets/List/ListContacts';
import { ListTemplate } from '../widgets/List/ListTemplate';
import { ListSelectWidget } from '../widgets/List/select/ListSelectWidget';
import { StatusTracker } from '../widgets/List/StatusTracker';
import { TransactionsList } from '../widgets/List/TransactionsList';
import { Mpi } from '../widgets/Mpi/Mpi';
import { SeparatorWidget } from '../widgets/Separator/SeparatorWidget';
import { BrazeBanner } from '../widgets/Stories/BrazeBanner';
import { PromoStories } from '../widgets/Stories/PromoStories';
import { AppVersionText } from '../widgets/Text/AppVersionText';
import { Balance } from '../widgets/Text/Balance';
import { TextLinked } from '../widgets/Text/TextLinked';
import { RowRegularWidget } from '../widgets/UI/Row/RowRegularWidget';
import { SelectorExtraWidget } from '../widgets/UI/Row/SelectorExtraWidget';
import { SelectorRegularWidget } from '../widgets/UI/Row/SelectorRegularWidget';
import { TextCommon } from '../widgets/UI/Text/TextCommon';

export const getWidgetById = (widgetId: WidgetEnum & TextInputEnum, widget: TWidget<TCommonData>, Package: React.ElementType) => {
    const { googleAuthClientId } = getGlobalVariables('googleAuthClientId');
    switch (widgetId) {
        case TextEnum.BALANCE:
            return <Balance widget={widget as TWidget<TBalanceData>} />;
        case TextEnum.TEXT:
            return <TextCommon isWidget={true} widget={widget as TWidget<TTextData>} />;
        case TextEnum.TEXT_LINKED:
            return <TextLinked options={widget.options} data={widget.data as TTextData} uid={widget.uid} />;
        case TextEnum.APP_VERSION:
            return <AppVersionText widget={widget} />;
        case LayoutEnum.WRAPPER_LAYOUT:
            return <Package uids={widget.widgets} widgets={widget.widgets} />;
        case LayoutEnum.VERTICAL_LAYOUT:
            return (
                <VerticalLayoutWidget widget={widget}>
                    <Package uids={widget.widgets} widgets={widget.widgets} />
                </VerticalLayoutWidget>
            );
        case LayoutEnum.VERTICAL_LAYOUT_WIDE:
            return (
                <VerticalLayoutWide widget={widget}>
                    <Package uids={widget.widgets} widgets={widget.widgets} />
                </VerticalLayoutWide>
            );
        case LayoutEnum.HORIZONTAL_LAYOUT:
            return (
                <HorizontalLayoutWidget widget={widget}>
                    <Package uids={widget.widgets} widgets={widget.widgets} />
                </HorizontalLayoutWidget>
            );
        case LayoutEnum.FLOATING_LAYOUT:
            return (
                <FloatingLayoutWidget widget={widget}>
                    <Package uids={widget.widgets} widgets={widget.widgets} />
                </FloatingLayoutWidget>
            );
        case LayoutEnum.SEPARATE_CONTENT_LAYOUT:
        case LayoutEnum.CALCULATOR_CONTENT_LAYOUT:
            return (
                <SeparateContentLayout
                    widget={widget}
                    topWidget={<Package uids={widget.widgets?.[0] ? [widget.widgets[0]] : []} />}
                    bottomWidget={<Package uids={widget.widgets?.[1] ? [widget.widgets[1]] : []} />}
                />
            );
        case LayoutEnum.WEB_VIEW_LAYOUT:
            return <WebViewLayout widget={widget as TWidget<TWebViewLayout, TWebViewLayoutOption>} />;
        case AnimationIconEnum.Success:
            return <SuccessAnimation widget={widget} />;
        case AnimationIconEnum.Error:
            return <ErrorAnimation widget={widget} />;
        case AnimationIconEnum.Loading:
            return <LoadingAnimation widget={widget} />;
        case CarouselEnum.CAROUSEL:
            return <CarouselWidget widget={widget as TWidget<TCarouselData>} />;
        case InputEnum.STROKED:
            return <StrokedInput widget={widget} />;
        case InputEnum.PHONE_TEXT_INPUT:
            return <InputPhoneWidget widget={widget as TWidget<TConsumerInputPhoneData, TInputOption>} />;
        case InputEnum.DATE_MASK_TEXT_INPUT:
            return <InputDate widget={widget} />;
        case TextInputEnum.ENTERPASSCODE:
            return <InputPasscode widget={widget as TWidget<TInputPasscodeData, TPasscodeOption>} />;
        case InputEnum.AUTO_SUBMIT_TEXT_INPUT:
            return <StrokedInput autoComplete="one-time-code" widget={widget} />;
        case InputEnum.SEARCH_ADDRESS_INPUT:
            return <InputSearchAddress widget={widget as TWidget<TInputSearchAddress, TInputOption>} />;
        case InputEnum.OTP_INPUT:
            return <OtpInputWidget widget={widget as TWidget<TInputData, TOTPOption>} />;
        case CardEnum.CARD_INPUT:
            return <CardInput widget={widget} />;
        case CardEnum.CARD_BALANCE:
            return <CardBalance widget={widget as TWidget<TCardBalanceData>} />;
        case CardEnum.CARD_VIEW:
            return <CardViewWidget widget={widget as TWidget<TCardView>} />;
        case ButtonEnum.BUTTON_PILL:
            return <ButtonPillWidget widget={widget as TWidget<TButtonData, TButtonOption>} />;
        case ButtonEnum.BUTTON_CIRCLE:
            return <ButtonCircleWidget widget={widget as TWidget<TButtonData, TButtonOption>} />;
        case ButtonEnum.BUTTON_ROW:
            return <ButtonRowWidget widget={widget} />;
        case ButtonEnum.ROW_EMPHASISED:
            return <RowEmphasised as="button" isWidget={true} widget={widget as TWidget<TButtonRowEmphasised, TRowOption>} />;
        case ButtonEnum.ROW_ACTION:
            return <RowActionWidget widget={widget as TWidget<TRowActionData, TRowOption>} />;
        case ButtonEnum.TIMER_BUTTON:
            return <TimerButton widget={widget as TWidget<TTimerButtonData, TButtonOption>} />;
        case ButtonEnum.BUTTON_ROW_TIMER:
            return <ButtonRowTimer widget={widget as TWidget<TTimerButtonData, TButtonOption>} />;
        case ButtonEnum.BUTTON_BANNER:
            return <ButtonBanner widget={widget} />;
        case ButtonEnum.BUTTON_MESSAGE:
            return <ButtonMessage widget={widget} />;
        case ButtonEnum.BUTTON_CIRCLE_ANIMATED:
            return <ButtonCircleAnimated widget={widget} />;
        case ButtonEnum.BUTTON_GOOGLE_PAY:
            return <ButtonGooglePay widget={widget as TWidget<TGooglePay>} />;
        case ButtonEnum.BUTTON_APPLE_PAY:
            return <ButtonApplePay widget={widget as TWidget<TApplePay>} />;
        case ButtonEnum.BUTTON_SIGN_IN_WITH_APPLE:
            return <ButtonSignInWithApple widget={widget as TWidget<TSignInWithAppleData, TButtonOption>} />;
        case ButtonEnum.BUTTON_SIGN_IN_WITH_GOOGLE:
            return (
                <GoogleOAuthProvider clientId={googleAuthClientId}>
                    <ButtonSignInWithGoogle widget={widget as TWidget<TSignInWithGoogleData, TSignInWithGoogleOptions>} />
                </GoogleOAuthProvider>
            );
        case ButtonEnum.BUTTON_MENU:
            return <ButtonMenu widget={widget} />;
        case ButtonEnum.INFO_BANNER:
            return <InfoBanner widget={widget as TWidget<TInfoBannerData, TInfoBannerOptions>} />;
        case ButtonEnum.DETAILS_SEPARATE_TEXT:
            return <DetailsSeparateText widget={widget as TWidget<TDetailsSeparateTextData, TDetailsSeparateTextOption>} />;
        case CheckboxEnum.SWITCHER:
            return <SwitcherWidget widget={widget} />;
        case CheckboxEnum.CHECKBOX:
            return <Checkbox isWidget={true} widget={widget} />;
        case DropdownEnum.DROPDOWN:
            return <Dropdown widget={widget} />;
        case ActionWidgetEnum.ACTION_WIDGET:
            return <Action widgetId={widgetId} widget={widget} />;
        case ListEnum.SELECT: {
            const data = (widget as TWidget<TConsumerListData, TListOption>).data;
            return <ListSelectWidget widget={widget as TWidget<TConsumerListData, TListOption>} rowId={data.cellWidgetId ?? RowEnum.ROW_REGULAR} />;
        }
        case ListEnum.TRANSACTIONS:
            return <TransactionsList widget={widget as TWidget<TConsumerListData, TListOption>} />;
        case ListEnum.CURRENCY:
            return <CurrencyListWidget widget={widget as TWidget<TConsumerListData, TListOption>} />;
        case ListEnum.CONTACTS:
            return <ListContacts widget={widget as TWidget<TConsumerListData, TListOption>} />;
        case ListEnum.PROFILE_MENU:
        case ListEnum.TEMPLATE:
            return <ListTemplate widget={widget as TWidget<TConsumerListData, TListOption>} Package={Package} />;
        case ListEnum.STATUS_TRACKER:
            return <StatusTracker widget={widget} />;
        case CalculatorEnum.CALCULATOR:
            return <Calculator widget={widget as TWidget<TCalculatorData>} />;
        case CalculatorEnum.TOP_UP:
            return <CalculatorTopUp widget={widget as TWidget<TCalculatorTopUpData>} />;
        case SeparatorEnum.SEPARATOR:
            return <SeparatorWidget widget={widget as TWidget<TSeparatorData, TSeparatorOption>} />;
        case ImageEnum.VISUAL:
            return <VisualWidget widget={widget} />;
        case ImageEnum.PROFILE_IMAGE:
            return <ProfileImage widget={widget} />;
        case StoriesEnum.STORIES:
            return <PromoStories widget={widget} />;
        case StoriesEnum.BRAZE_BANNER:
            return <BrazeBanner widget={widget as TWidget<TBrazeBanner, TBrazeOption>} />;
        case MpiEnum.MPI:
            return <Mpi widget={widget as TWidget<TMpiData>} />;
        case KycEnum.SUMSUB:
            return <Sumsub widget={widget as TWidget<TKYC>} />;
        case KycEnum.ZENDESK_CHAT_WIDGET:
            return <ZendeskChat widget={widget as TWidget<TKYC>} />;
        case BarEnum.LIMIT_BAR:
            return <LimitBar widget={widget as TWidget<TBar>} />;
        case BarEnum.REFERRALS_PROGRESS:
            return <ReferralsProgress widget={widget as TWidget<TReferralsProgressData>} />;
        case RowEnum.SELECTOR_REGULAR:
            return <SelectorRegularWidget widget={widget as TWidget<TSelectorRegularData, TRowOption & TSelectorRegularOption>} />;
        case RowEnum.ROW_REGULAR:
            return <RowRegularWidget widget={widget as TWidget<TSelectorRegularData, TRowOption & TSelectorRegularOption>} />;
        case RowEnum.SELECTOR_EXTRA:
            return <SelectorExtraWidget widget={widget as TWidget<TSelectorRegularData, TRowOption & TSelectorRegularOption>} />;
        default:
            return false;
    }
};
